import {combineReducers, compose, configureStore} from '@reduxjs/toolkit';
import {
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from 'redux-persist';
import darkModeReducer from './slices/theme';
import authReducer from './slices/auth';
import userInfosReducer from './slices/user-infos';
import sportsReducer from './slices/sports';
import groupsReducer from './slices/groups';
import eventsReducer from './slices/events';
import modulesReducer from './slices/modules';
import companyReducer from './slices/company';
import locationsReducer from './slices/admin/locations';
import networkStatusReducer from './slices/network-status';
import adminCompaniesReducer from './slices/admin/companies';
import matrixUserReducer from './slices/matrix/user-data';
import matrixRoomsReducer from './slices/matrix/room-data';
import filterReducer from './slices/filter';
import billingDataReducer from './slices/billing-data';
import odooCompanyReducer from './slices/odoo-company';
import onDemandEventsReducer from './slices/on-demand-events';
import {TypedUseSelectorHook, useDispatch, useSelector} from 'react-redux';
import {env} from '@/config';

const rootReducer = combineReducers({
  theme: darkModeReducer,
  auth: authReducer,
  userInfos: userInfosReducer,
  sports: sportsReducer,
  groups: groupsReducer,
  events: eventsReducer,
  modules: modulesReducer,
  company: companyReducer,
  locations: locationsReducer,
  networkStatus: networkStatusReducer,
  adminCompanies: adminCompaniesReducer,
  matrixUser: matrixUserReducer,
  matrixRooms: matrixRoomsReducer,
  filter: filterReducer,
  billingData: billingDataReducer,
  odooCompany: odooCompanyReducer,
  onDemandEvents: onDemandEventsReducer
});

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

const {ENVIRONMENT} = env;

const composeEnhancers =
  ENVIRONMENT === 'production'
    ? compose
    : window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }),
  enhancers: composeEnhancers,
  devTools: ENVIRONMENT === 'development'
});

const persistor = persistStore(store);

export {store, persistor};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

type DispatchFunc = () => AppDispatch;
export const useAppDispatch: DispatchFunc = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
